import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

/* ── Collapsible Section ── */
const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}>
        <span>{title}</span><span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

/* ── Read-only info field ── */
const InfoField = ({ label, value, mono = false, badge = null }) => (
  <div>
    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>
      {label}
    </div>
    {badge ? badge : (
      <div className={`form-control bg-light${mono ? " font-monospace" : ""}`}
        style={{ fontSize: 13, color: "#374151", minHeight: 38 }}>
        {value || "—"}
      </div>
    )}
  </div>
);

/* ── Status config ── */
const STATUS_OPTIONS = ["COMPLETED", "CANCELLED", "PENDING"];
const STATUS_STYLE = {
  COMPLETED: { bg: "#d1e7dd", color: "#0a3622" },
  CANCELLED: { bg: "#f8d7da", color: "#842029" },
  PENDING:   { bg: "#fff3cd", color: "#856404" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg: "#e9ecef", color: "#495057" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 12px", borderRadius: 20,
      fontSize: 12, fontWeight: 600, background: s.bg, color: s.color,
    }}>{status || "—"}</span>
  );
};

/* ═══════════════════════════════════════════════════════════ */
export default function EditTransfer() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const token     = localStorage.getItem("token");

  /* ── Loaded record ── */
  const [transfer,  setTransfer]  = useState(null);
  const [loading,   setLoading]   = useState(true);

  /* ── Editable fields (only what the backend PUT allows) ── */
  const [status,            setStatus]            = useState("COMPLETED");
  const [reasonForTransfer, setReasonForTransfer] = useState("");
  const [dispatchDetails,   setDispatchDetails]   = useState("");

  /* ── UI ── */
  const [submitting, setSubmitting] = useState(false);

  /* ── Tabs ── */
  const [activeTab, setActiveTab] = useState("systemNotes");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["systemNotes"]));
  const [navbarColor, setNavbarColor] = useState("#080235");

  /* ── Bootstrap ── */
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token)                        { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized.");          navigate("/"); }
  }, [navigate, token]);

  /* ── Navbar Color ── */
  useEffect(() => {
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;
      fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  /* ── Load transfer by ID ── */
  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/asset-transfers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 404) throw new Error("Transfer record not found.");
        if (!r.ok)            throw new Error("Failed to load transfer record.");
        return r.json();
      })
      .then(data => {
        setTransfer(data);
        setStatus(data.status            || "COMPLETED");
        setReasonForTransfer(data.reasonForTransfer || "");
        setDispatchDetails(data.dispatchDetails     || "");
      })
      .catch(err => {
        toast.error(err.message || "Failed to load transfer record.");
        navigate("/alltransfers");
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  /* ── Submit PUT ── */
  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-transfers/${id}`, {
        method:  "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status, reasonForTransfer, dispatchDetails }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update transfer.");
      }
      toast.success("Transfer record updated successfully.");
      setTimeout(() => navigate("/alltransfers"), 1200);
    } catch (err) {
      toast.error(err.message || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <>
        <div className="fixed-top"><AssetModuleNavbar /></div>
        <div className="container-fluid" style={{ paddingTop: 120 }}>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="text-muted mt-3" style={{ fontSize: 14 }}>Loading transfer record…</div>
          </div>
        </div>
      </>
    );
  }

  /* ═══════════════════════════ RENDER ═══════════════════════════ */
  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* ── Page Header ── */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-pencil-square me-2 text-warning"></i>
              Edit Transfer Record
            </h4>
            <div className="d-flex gap-2 mt-3 align-items-center">
            <button className="btn btn-primary" type="submit" form="edit-transfer-form" disabled={submitting} style={{ minWidth: 50 }}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : <> Save </>}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
          </div>
        </div>

        {/* ── Info banner ── */}
        <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13 }}>
          <i className="bi bi-info-circle-fill"></i>
          <span>
            Asset, parties, transfer type, date and condition are <strong>locked</strong> after creation.
            You can update the <strong>status</strong>, <strong>reason</strong>, and <strong>dispatch details</strong> below.
          </span>
        </div>

        <form id="edit-transfer-form" onSubmit={handleSubmit}>

          {/* ── SECTION 1: Locked transfer info (read-only) ── */}
          <Section title="Transfer Information (Read-only)">
            <div className="col-md-3">
              <InfoField label="Asset Code" value={transfer?.assetCode} mono />
            </div>
            <div className="col-md-3">
              <InfoField label="Asset Name" value={transfer?.assetName} />
            </div>
            <div className="col-md-3">
              <InfoField label="Transfer Type" value={transfer?.transferType} />
            </div>
            <div className="col-md-3">
              <InfoField label="Transfer Date" value={transfer?.transferDate} />
            </div>
            <div className="col-md-4">
              <InfoField label="From (Previous Holder)" value={transfer?.fromEmployeeName} />
            </div>
            <div className="col-md-4">
              <InfoField label="To (Recipient)" value={transfer?.toEmployeeName} />
            </div>
            <div className="col-md-4">
              <InfoField label="Condition at Transfer" value={transfer?.conditionAtTransfer} />
            </div>
            <div className="col-md-3">
              <InfoField label="Created At" value={transfer?.createdAt ? new Date(transfer.createdAt).toLocaleString() : "—"} />
            </div>
          </Section>

          {/* ── SECTION 2: Editable fields ── */}
          <Section title="Editable Fields">

            {/* Status */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">
                Status <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <div className="mt-2">
                <StatusBadge status={status} />
              </div>
            </div>

            {/* Spacer */}
            <div className="col-md-9"></div>

            {/* Reason for Transfer */}
            <div className="col-md-12">
              <label className="form-label fw-semibold">Reason for Transfer</label>
              <textarea
                className="form-control"
                rows={3}
                value={reasonForTransfer}
                onChange={e => setReasonForTransfer(e.target.value)}
                placeholder="Explain the transfer requirement and any business context…"
              />
            </div>

            {/* Dispatch Details */}
            <div className="col-md-12">
              <label className="form-label fw-semibold">Dispatch Details</label>
              <textarea
                className="form-control"
                rows={2}
                value={dispatchDetails}
                onChange={e => setDispatchDetails(e.target.value)}
                placeholder="Courier, handover note, box condition, receiving details…"
              />
            </div>
          </Section>

          {/* ── System Information Tabs ── */}
          <ul className="nav nav-tabs mb-0 fw-semibold mt-4" style={{ backgroundColor: navbarColor }}>
            {[["systemNotes", "System Notes"]].map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(key);
                    setVisitedTabs(prev => new Set(prev).add(key));
                  }}
                  style={{
                    color: activeTab === key ? navbarColor : "#fff",
                    backgroundColor: activeTab === key ? "#fff" : "transparent",
                    fontWeight: activeTab === key ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <div className="border p-3 bg-white mb-4">
            <div className="text-muted small mb-2">
              <strong>Date Created:</strong>{" "}
              {transfer?.createdAt ? new Date(transfer.createdAt).toLocaleString() : "—"}
            </div>
            {visitedTabs.has("systemNotes") && (
              <div style={{ display: activeTab === "systemNotes" ? "block" : "none" }}>
                <SystemNotes entityType="TRANSFER" entityId={id} token={token} />
              </div>
            )}
          </div>

          {/* ── Action Buttons ── */}
          <div className="d-flex gap-2 mt-3 mb-5 align-items-center">
            <button className="btn btn-primary" type="submit" disabled={submitting} style={{ minWidth: 50 }}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : <> Save </>}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
